import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { FiCode, FiEdit3, FiGlobe, FiSave, FiTrash2, FiX, FiZap } from "react-icons/fi";
import type { ContentDocument, ContentTypeConfig } from "../API/Models";
import { client } from "../Clients";
import { PreviewLinks, StatusBadge } from "./Content/ContentListView";
import { usePageHeader } from "../hooks/usePageHeader";
import ContentItemForm, { validateContentItemValues } from "../Components/ContentItemForm";
import { Modal } from "../Components/Modal";
import { showSuccessModal } from "../Helper/showSuccessModal";
import type { IApplicationState } from "../Redux/Reducers";
import TaxonomyMultiSelect from "../Components/TaxonomyMultiSelect";
import CategoryPathSelect, { buildCategoryPathLabel } from "../Components/CategoryPathSelect";
import AIAnalyzeModal from "../Components/AIAnalyzeModal";
import ContentHistoryPanel from "../Components/ContentHistory/ContentHistoryPanel";

const DEFAULT_LANGUAGE = "fr";

const normalizeContentTypeKey = (value?: string | null) =>
	(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

const resolveContentTypeConfig = (configs: ContentTypeConfig[], idOrKey?: string | null) => {
	if (!idOrKey) return null;
	const normalized = idOrKey.trim().toLowerCase();
	const byId = configs.find((item) => (item.id ?? "").toLowerCase() === normalized);
	if (byId) return byId;
	return configs.find((item) => normalizeContentTypeKey(item.title ?? "") === normalized) ?? null;
};

const extractValues = (data: any) => {
	if (!data || typeof data !== "object") return {};
	return { ...data };
};

/** Individual content editor with dynamic form, taxonomies, categories, AI analysis and translation. */
const ContentItem: React.FC = () => {
	const { contentTypeId, itemId } = useParams<{ contentTypeId: string; itemId: string }>();
	const history = useHistory();
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);
	const contentTypeConfigsInitialized = useSelector((state: IApplicationState) => state.contentTypeConfigs.initialized);
	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);
	const categories = useSelector((state: IApplicationState) => state.categories.items);
	const decodedContentTypeId = useMemo(() => {
		try {
			return decodeURIComponent(contentTypeId);
		} catch {
			return contentTypeId;
		}
	}, [contentTypeId]);
	const decodedItemId = useMemo(() => {
		try {
			return decodeURIComponent(itemId);
		} catch {
			return itemId;
		}
	}, [itemId]);
	const config = useMemo(
		() => resolveContentTypeConfig(contentTypeConfigs ?? [], decodedContentTypeId),
		[contentTypeConfigs, decodedContentTypeId]
	);
	const schemaKey = useMemo(() => {
		if (config?.id) return config.id;
		if (!contentTypeConfigsInitialized) return null;
		return decodedContentTypeId?.trim() ?? null;
	}, [config?.id, contentTypeConfigsInitialized, decodedContentTypeId]);
	const [draft, setDraft] = useState<ContentDocument | null>(null);
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [formError, setFormError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [editing, setEditing] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [showJson, setShowJson] = useState(false);
	const [showAnalyze, setShowAnalyze] = useState(false);
	const [deployLinks, setDeployLinks] = useState<{ preview: string[]; production: string[] } | null>(null);
	const lastLoadKey = useRef<string | null>(null);

	usePageHeader({ title: config?.title ?? "Content" });

	const loadItem = useCallback(async () => {
		if (!schemaKey) return;
		setLoading(true);
		setFormError(null);
		try {
			const response = await client.content.apiContentSchemaKeyLanguageGet(
				schemaKey,
				DEFAULT_LANGUAGE,
				decodedItemId
			);
			const current = response.data?.current ?? null;
			setDraft(current);
			setFormValues(extractValues(current?.data));
			setFieldErrors({});
		} catch (err: any) {
			console.error(err);
			setFormError("Unable to load content.");
		} finally {
			setLoading(false);
		}
	}, [decodedItemId, schemaKey]);

	useEffect(() => {
		if (!schemaKey) return;
		const key = `${schemaKey}:${decodedItemId}`;
		if (lastLoadKey.current === key) return;
		lastLoadKey.current = key;
		loadItem();
	}, [decodedItemId, loadItem, schemaKey]);

	const hasConfig = Boolean(config?.fields?.length);
	const canEdit = Boolean(config);
	const supportsTaxonomy = Boolean(config?.supportsTaxonomy);
	const supportsCategory = Boolean(config?.supportsCategory) && (categories ?? []).length > 0;
	const taxonomyIds = useMemo(() => (formValues?.taxonomyIds ?? []).filter(Boolean), [formValues]);
	const categoryPathIds = useMemo(
		() => (formValues?.categoryPathIds ?? []).filter(Boolean),
		[formValues]
	);
	const taxonomyMap = useMemo(() => {
		const map = new Map<string, string>();
		taxonomies.forEach((taxonomie) => {
			if (taxonomie.id) {
				map.set(taxonomie.id, taxonomie.title ?? taxonomie.id);
			}
		});
		return map;
	}, [taxonomies]);
	const taxonomyLabels = useMemo(
		() => taxonomyIds.map((id) => taxonomyMap.get(id) ?? id),
		[taxonomyIds, taxonomyMap]
	);
	const categoryLabel = useMemo(
		() => buildCategoryPathLabel(categories ?? [], categoryPathIds),
		[categories, categoryPathIds]
	);
	const pathValue = useMemo(() => {
		const draftPath = (draft?.data as any)?.path;
		const formPath = (formValues as any)?.path;
		return (editing ? formPath ?? draftPath : draftPath) ?? "";
	}, [draft?.data, editing, formValues]);
	const jsonPayload = useMemo(() => {
		const source = editing ? formValues : draft?.data;
		return JSON.stringify(source ?? {}, null, 2);
	}, [draft?.data, editing, formValues]);

	// Resolve the REAL deployed URLs (per target, from the deployed-files ground truth) by the
	// item's stable content id — the same id the deploy wrote next to each resolved URL. This is
	// collision-free (unlike matching the record's ambiguous stored path) and gives honest
	// Live/Preview/Draft status + working Dev/Prod links + a genuine dual-location warning.
	useEffect(() => {
		if (!decodedItemId) {
			setDeployLinks(null);
			return;
		}
		let cancelled = false;
		const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");
		axios
			.get<{ preview?: string[]; production?: string[] }>(`${apiBase}/api/content/deployment-links`, {
				params: { id: decodedItemId },
				withCredentials: true
			})
			.then((r) => {
				if (cancelled) return;
				setDeployLinks({
					preview: Array.isArray(r.data?.preview) ? r.data.preview : [],
					production: Array.isArray(r.data?.production) ? r.data.production : []
				});
			})
			.catch(() => {
				if (!cancelled) setDeployLinks(null);
			});
		return () => {
			cancelled = true;
		};
	}, [decodedItemId]);

	const handleStartEdit = () => {
		setEditing(true);
		setFormValues(extractValues(draft?.data));
		setFieldErrors({});
		setFormError(null);
	};

	const handleCancelEdit = () => {
		setEditing(false);
		setFormValues(extractValues(draft?.data));
		setFieldErrors({});
		setFormError(null);
	};

	const handleSave = async () => {
		if (!config || !schemaKey) return;
		const validation = validateContentItemValues(config.fields ?? [], formValues);
		setFieldErrors(validation.errors);
		if (validation.hasErrors) {
			setFormError("Please fix the highlighted fields.");
			return;
		}
		setFormError(null);
		setSaving(true);
		try {
			const payloadDoc: ContentDocument = {
				contentType: schemaKey,
				language: DEFAULT_LANGUAGE,
				id: draft?.id ?? decodedItemId,
				isDisabled: draft?.isDisabled ?? false,
				isDeleted: false,
				data: formValues
			};
			const response = await client.content.apiContentSchemaKeyLanguagePut(
				schemaKey,
				DEFAULT_LANGUAGE,
				decodedItemId,
				payloadDoc
			);
			const current = response.data?.current ?? payloadDoc;
			setDraft(current);
			setEditing(false);
			showSuccessModal({
				title: "Content updated",
				message: `${config.title ?? "Content"} has been updated.`
			});
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? "Save failed.";
			setFormError(String(apiMessage));
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!config || !schemaKey) return;
		setDeleting(true);
		setFormError(null);
		try {
			const payloadDoc: ContentDocument = {
				contentType: schemaKey,
				language: DEFAULT_LANGUAGE,
				id: draft?.id ?? decodedItemId,
				isDisabled: draft?.isDisabled ?? false,
				isDeleted: true,
				data: draft?.data ?? {}
			};
			await client.content.apiContentSchemaKeyLanguagePut(schemaKey, DEFAULT_LANGUAGE, decodedItemId, payloadDoc);
			showSuccessModal({
				title: "Content deleted",
				message: `${config.title ?? "Content"} has been deleted.`
			});
			history.push(`/content/${encodeURIComponent(contentTypeId)}`);
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? "Delete failed.";
			setFormError(String(apiMessage));
		} finally {
			setDeleting(false);
			setShowDeleteConfirm(false);
		}
	};

	const renderBody = () => {
		if (loading) return <p className="text-sm text-slate-600">Loading content…</p>;
		if (!contentTypeConfigsInitialized && !config) return <p className="text-sm text-slate-600">Loading content type…</p>;
		if (!config) return <p className="text-sm text-slate-600">This content type is unavailable.</p>;

		return (
			<div className="space-y-4">
				{formError && (
					<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
						{formError}
					</div>
				)}
				{(config.supportsDynamicPath || pathValue) && (
					<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Path</p>
						<div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-mono text-slate-700">
							{pathValue || (editing ? "Path will be generated on save." : "No path generated yet.")}
						</div>
					</div>
				)}
				<ContentItemForm
					fields={config.fields ?? []}
					value={formValues}
					onChange={setFormValues}
					errors={fieldErrors}
					disabled={!editing || saving}
				/>
				{supportsCategory && (
					<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
						<div className="flex items-center justify-between">
							<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Categories</p>
						</div>
						{editing ? (
							<CategoryPathSelect
								categories={categories}
								value={categoryPathIds}
								onChange={(next) => setFormValues((prev) => ({ ...prev, categoryPathIds: next }))}
								placeholder="Assign category path"
								helper="Choose a single category path for this content."
								disabled={saving}
							/>
						) : (
							<div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
								{categoryLabel ? categoryLabel : "No category assigned."}
							</div>
						)}
					</div>
				)}
				{supportsTaxonomy && (
					<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
						<div className="flex items-center justify-between">
							<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Taxonomies</p>
						</div>
						{editing ? (
							<TaxonomyMultiSelect
								options={taxonomies}
								value={taxonomyIds}
								onChange={(next) => setFormValues((prev) => ({ ...prev, taxonomyIds: next }))}
								placeholder="Assign taxonomies"
								helper="Filter and select taxonomies for this content."
							/>
						) : (
							<div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
								{taxonomyLabels.length ? taxonomyLabels.join(", ") : "No taxonomies assigned."}
							</div>
						)}
					</div>
				)}
				{!hasConfig && <p className="text-sm text-slate-600">No fields are defined for this content type.</p>}
			</div>
		);
	};

	return (
		<div className="p-6 space-y-6">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Content</p>
					<h1 className="text-xl font-semibold text-slate-900">{config?.title ?? "Content"}</h1>
					<p className="text-sm text-slate-600">{config?.description ?? "Edit content data for this item."}</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{deployLinks && (
						<div className="mr-1 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
							<StatusBadge preview={deployLinks.preview.length > 0} prod={deployLinks.production.length > 0} />
							<PreviewLinks previewUrls={deployLinks.preview} productionUrls={deployLinks.production} />
						</div>
					)}
					{editing ? (
						<>
							<button
								type="button"
								onClick={handleCancelEdit}
								disabled={saving}
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
							>
								<FiX /> Cancel
							</button>
							<button
								type="button"
								onClick={handleSave}
								disabled={saving || !canEdit}
								className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
							>
								<FiSave /> {saving ? "Saving…" : "Save"}
							</button>
						</>
					) : (
						<>
							<button
								type="button"
								onClick={() => setShowAnalyze(true)}
								disabled={!draft}
								className="inline-flex items-center gap-1 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-100 disabled:opacity-60"
							>
								<FiZap /> Analyze
							</button>
							<Link
								to={`/traduction/${encodeURIComponent(schemaKey ?? "")}?slug=${encodeURIComponent(decodedItemId)}&lang=en`}
								className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
							>
								<FiGlobe /> Traduire
							</Link>
							<button
								type="button"
								onClick={() => setShowJson(true)}
								disabled={!draft}
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
							>
								<FiCode /> JSON
							</button>
							<button
								type="button"
								onClick={handleStartEdit}
								disabled={!canEdit}
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
							>
								<FiEdit3 /> Edit
							</button>
						</>
					)}
					{editing && (
						<button
							type="button"
							onClick={() => setShowDeleteConfirm(true)}
							disabled={!canEdit || deleting}
							className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-60"
						>
							<FiTrash2 /> Delete
						</button>
					)}
				</div>
			</div>
			<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">{renderBody()}</div>
			<ContentHistoryPanel contentId={draft?.id ?? decodedItemId} />

			<Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Delete content?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">
						Are you sure you want to delete this content? This action cannot be undone.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setShowDeleteConfirm(false)}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={() => handleDelete()}
						className="inline-flex items-center rounded-full border border-rose-200 bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
						disabled={deleting}
					>
						{deleting ? "Deleting…" : "Delete"}
					</button>
				</Modal.Footer>
			</Modal>
			<Modal show={showJson} onHide={() => setShowJson(false)} size="lg" centered>
				<Modal.Header closeButton>
					<Modal.Title>Content JSON</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<pre className="max-h-[65vh] overflow-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-100">
						{jsonPayload}
					</pre>
				</Modal.Body>
			</Modal>
			<AIAnalyzeModal
				show={showAnalyze}
				onClose={() => setShowAnalyze(false)}
				schemaKey={schemaKey ?? ""}
				language={DEFAULT_LANGUAGE}
				contentId={draft?.id ?? decodedItemId}
				data={draft?.data ?? {}}
				analyzableFields={
					config?.fields
						?.filter((f: any) => f.analyzeMistakes || f.analyzeSeo)
						.map((f: any) => f.key)
						.filter(Boolean) ?? undefined
				}
				cachedResult={(draft as any)?.analysisResult ?? null}
				cachedHash={(draft as any)?.analysisHash ?? null}
				onResultCached={async (resultJson, hash) => {
					try {
						// Save analysis result back to the content document
						const payloadDoc = {
							contentType: schemaKey,
							language: DEFAULT_LANGUAGE,
							id: draft?.id ?? decodedItemId,
							isDisabled: draft?.isDisabled ?? false,
							isDeleted: false,
							data: { ...(draft?.data ?? {}), analysisResult: resultJson, analysisHash: hash }
						};
						await client.content.apiContentSchemaKeyLanguagePut(
							schemaKey ?? "", DEFAULT_LANGUAGE, decodedItemId, payloadDoc as any
						);
					} catch (err) {
						console.warn("Failed to persist analysis result:", err);
					}
				}}
			/>
		</div>
	);
};

export default ContentItem;
